import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { SiteContent, ContentContextType } from '../types';
import { COMPANY_NAME_CN, COMPANY_NAME_EN, SERVICES_DATA, EXPERIENCE_DATA, TEAM_DATA, ADDRESS, CULTURE_ITEMS, PROCESS_DATA } from '../constants';

// 本地 Logo 路径配置
// 请确保 images/logo.png 文件存在于项目根目录下
const BRAND_LOGO_PATH = './images/logo.png';

const defaultContent: SiteContent = {
  hero: {
    companyNameCn: COMPANY_NAME_CN,
    companyNameEn: COMPANY_NAME_EN,
    tagline: "合规为基 · 价值为核 —— 您的战略级财税伙伴",
    subTagline: "Compliance as Foundation, Value as Core - Your Strategic Tax Partner",
    logoUrl: BRAND_LOGO_PATH,
    backgroundImage: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=1920"
  },
  about: {
    intro: [
      "湖南剑云税务师事务所有限公司（简称“剑云税务”）是一家深耕财税服务领域、以专业赋能企业成长的综合性咨询机构。我们紧跟金税工程迭代与税收立法进程，始终将“合规为基、价值为核”作为服务初心。",
      "剑云税务核心团队汇聚了注册会计师、税务师、CMA等多位资深专家，平均从业经验超10年。我们依托全品类专业资质，构建了覆盖企业全生命周期的服务矩阵。",
      "本所坚持“技术赋能专业”的发展理念，引入数字化服务平台，实现账务处理自动化、风险排查智能化。我们将以高水平的执业质量，助力企业行稳致远。"
    ],
    culture: CULTURE_ITEMS,
    image: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=1200"
  },
  team: {
    show: true,
    members: TEAM_DATA
  },
  services: SERVICES_DATA,
  process: PROCESS_DATA,
  experience: EXPERIENCE_DATA,
  contact: {
    address: ADDRESS,
    phone: "专业团队 随叫随到",
    email: "service@hnjianyun.cn",
    qrCodeUrl: "", // 默认为空，不显示
    qrCodeLabel: "扫码关注公众号"
  },
  footer: {
    description: "剑云税务致力于为不同行业企业提供全周期、定制化财税解决方案。以“1+N”模式为核心，解决问题不推诿，专家团队做后盾。",
    icpText: "湘ICP备XXXXXXXX号",
    icpLink: "https://beian.miit.gov.cn/",
    policeText: "湘公网安备 XXXXXXXXXXXXXX号",
    policeLink: "http://www.beian.gov.cn/portal/registerSystemInfo"
  }
};

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export function ContentProvider({ children }: { children?: React.ReactNode }) {
  const [content, setContent] = useState<SiteContent>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('site_content_final_v100');
        if (saved) {
          const parsed = JSON.parse(saved);
          return { 
            ...defaultContent, 
            ...parsed,
            hero: {
              ...defaultContent.hero,
              ...(parsed.hero || {}),
            }
          };
        }
        return defaultContent;
      } catch (e) {
        return defaultContent;
      }
    }
    return defaultContent;
  });

  useEffect(() => {
    localStorage.setItem('site_content_final_v100', JSON.stringify(content));
  }, [content]);

  const updateContent = useCallback((section: keyof SiteContent, data: any) => {
    setContent(prev => ({ ...prev, [section]: data }));
  }, []);

  const overrideContent = useCallback((newContent: SiteContent) => {
    setContent(newContent);
  }, []);

  const resetToDefaults = useCallback(() => {
    if (confirm('确定要恢复出厂设置吗？所有修改将丢失。')) {
      localStorage.removeItem('site_content_final_v100');
      window.location.reload();
    }
  }, []);

  const value = useMemo(() => ({
    content,
    updateContent,
    overrideContent,
    resetToDefaults
  }), [content, updateContent, overrideContent, resetToDefaults]);

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const context = useContext(ContentContext);
  if (!context) throw new Error('useContent must be used within a ContentProvider');
  return context;
}